import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Camera,
  Mail,
  Phone,
  MapPin,
  Calendar,
  ExternalLink,
  Instagram,
  Facebook,
  Globe,
  Loader2,
  ArrowRight,
  Image as ImageIcon
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Theme configurations
const THEMES = {
  dark: {
    bg: 'bg-black',
    text: 'text-white',
    muted: 'text-gray-400',
    card: 'bg-zinc-900',
    border: 'border-zinc-800'
  },
  light: {
    bg: 'bg-white',
    text: 'text-gray-900',
    muted: 'text-gray-500',
    card: 'bg-gray-50',
    border: 'border-gray-200'
  },
  warm: {
    bg: 'bg-amber-50',
    text: 'text-amber-900',
    muted: 'text-amber-600',
    card: 'bg-white',
    border: 'border-amber-200'
  },
  cool: {
    bg: 'bg-slate-900',
    text: 'text-slate-100',
    muted: 'text-slate-400',
    card: 'bg-slate-800',
    border: 'border-slate-700'
  }
};

// Layout configurations
const LAYOUTS = {
  modern: 'Modern',
  classic: 'Klasyczny',
  minimal: 'Minimalistyczny',
  bold: 'Wyrazisty'
};

export default function PhotographerPortfolioPage() {
  const { subdomain } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedGallery, setSelectedGallery] = useState(null);

  useEffect(() => {
    fetchPhotographerData();
  }, [subdomain]);

  const fetchPhotographerData = async () => {
    try {
      const response = await axios.get(`${API}/p/${subdomain}`);
      setData(response.data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Fotograf nie znaleziony' : 'Błąd ładowania');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Camera className="w-16 h-16 mx-auto mb-4 text-gray-600" />
          <h1 className="text-2xl font-bold text-white mb-2">{error}</h1>
          <Link to="/">
            <Button variant="outline">Strona główna</Button>
          </Link>
        </div>
      </div>
    );
  }

  const { photographer, galleries, featured_photos, services } = data;
  const settings = photographer.portfolio_settings || {};
  const branding = photographer.branding || {};
  const theme = THEMES[settings.theme] || THEMES.dark;
  const accentColor = settings.accent_color || branding.primary_color || '#FAFAFA';

  return (
    <div className={`min-h-screen ${theme.bg}`}>
      {/* Hero Section */}
      <header className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        {/* Background with featured photos */}
        <div className="absolute inset-0 grid grid-cols-3 gap-1 opacity-20">
          {featured_photos.slice(0, 9).map((photo, idx) => (
            <div key={idx} className="aspect-square overflow-hidden">
              <img 
                src={photo.thumbnail_url || photo.url} 
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
        
        {/* Gradient overlay */}
        <div className={`absolute inset-0 bg-gradient-to-b ${theme.bg === 'bg-black' ? 'from-black/60 via-black/80 to-black' : 'from-white/60 via-white/80 to-white'}`} />
        
        {/* Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          {branding.logo_url ? (
            <img 
              src={branding.logo_url} 
              alt={photographer.name}
              className="h-20 mx-auto mb-6"
            />
          ) : (
            <Camera className={`w-16 h-16 mx-auto mb-6 ${theme.text}`} style={{ color: accentColor }} />
          )}
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`text-5xl md:text-7xl font-bold mb-4 ${theme.text}`}
            style={{ fontFamily: settings.font_family || branding.font_family || 'Manrope' }}
          >
            {settings.hero_title || photographer.name}
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`text-xl md:text-2xl ${theme.muted} mb-8`}
          >
            {settings.hero_subtitle || 'Profesjonalna fotografia'}
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex justify-center gap-4 flex-wrap"
          >
            {galleries.length > 0 && (
              <Button
                size="lg"
                style={{ backgroundColor: accentColor, color: theme.bg === 'bg-black' ? '#000' : '#fff' }}
                onClick={() => document.getElementById('galleries')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Zobacz galerie
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            <Link to={`/b/${subdomain}`}>
              <Button
                size="lg"
                variant={galleries.length > 0 ? "outline" : "default"}
                className={galleries.length > 0 ? theme.text : ''}
                style={galleries.length === 0 ? { backgroundColor: accentColor, color: theme.bg === 'bg-black' ? '#000' : '#fff' } : {}}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Zarezerwuj sesję
              </Button>
            </Link>
            {settings.show_contact && (
              <Button
                variant="outline"
                size="lg"
                className={theme.text}
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Kontakt
              </Button>
            )}
          </motion.div>
          
          {/* Social Links */}
          {settings.social_links && Object.keys(settings.social_links).length > 0 && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="flex justify-center gap-4 mt-8"
            >
              {settings.social_links.instagram && (
                <a href={settings.social_links.instagram} target="_blank" rel="noopener noreferrer" 
                   className={`${theme.muted} hover:${theme.text} transition-colors`}>
                  <Instagram className="w-6 h-6" />
                </a>
              )}
              {settings.social_links.facebook && (
                <a href={settings.social_links.facebook} target="_blank" rel="noopener noreferrer"
                   className={`${theme.muted} hover:${theme.text} transition-colors`}>
                  <Facebook className="w-6 h-6" />
                </a>
              )}
              {settings.social_links.website && (
                <a href={settings.social_links.website} target="_blank" rel="noopener noreferrer"
                   className={`${theme.muted} hover:${theme.text} transition-colors`}>
                  <Globe className="w-6 h-6" />
                </a>
              )}
            </motion.div>
          )}
        </div>
      </header>

      {/* About Section */}
      {settings.about_text && (
        <section className={`py-20 px-6 ${theme.card}`}>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className={`text-3xl font-bold mb-6 ${theme.text}`}>O mnie</h2>
            <p className={`text-lg leading-relaxed ${theme.muted}`}>
              {settings.about_text}
            </p>
          </div>
        </section>
      )}

      {/* Featured Photos Grid */}
      {featured_photos.length > 0 && (
        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <h2 className={`text-3xl font-bold mb-10 text-center ${theme.text}`}>
              Wybrane prace
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {featured_photos.slice(0, 12).map((photo, idx) => (
                <motion.div
                  key={photo.id || idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  className="aspect-square overflow-hidden rounded-lg group cursor-pointer"
                  onClick={() => setSelectedGallery(photo.gallery_id)}
                >
                  <img 
                    src={photo.thumbnail_url || photo.url}
                    alt={photo.original_filename || 'Zdjęcie'}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Galleries Section */}
      {settings.show_galleries !== false && galleries.length > 0 && (
        <section id="galleries" className={`py-20 px-6 ${theme.card}`}>
          <div className="max-w-7xl mx-auto">
            <h2 className={`text-3xl font-bold mb-10 text-center ${theme.text}`}>
              Galerie
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleries.map((gallery, idx) => (
                <motion.div
                  key={gallery.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Link to={`/g/${gallery.public_id}`}>
                    <Card className={`${theme.card} ${theme.border} overflow-hidden group cursor-pointer hover:shadow-xl transition-shadow`}>
                      <div className="aspect-video bg-gray-800 relative overflow-hidden">
                        {gallery.cover_url ? (
                          <img 
                            src={gallery.cover_url}
                            alt={gallery.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="w-12 h-12 text-gray-600" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Button variant="secondary" size="sm">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Zobacz
                          </Button>
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <h3 className={`font-semibold mb-1 ${theme.text}`}>{gallery.name}</h3>
                        <p className={`text-sm ${theme.muted}`}>{gallery.client_name}</p>
                        <p className={`text-xs ${theme.muted} mt-2`}>
                          {gallery.photo_count || 0} zdjęć
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services Section */}
      {settings.show_services !== false && services.length > 0 && (
        <section className="py-20 px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className={`text-3xl font-bold mb-10 text-center ${theme.text}`}>
              Usługi
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, idx) => (
                <motion.div
                  key={service.id || idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card className={`${theme.card} ${theme.border} p-6 h-full`}>
                    <h3 className={`text-xl font-semibold mb-2 ${theme.text}`}>{service.name}</h3>
                    <p className={`text-sm ${theme.muted} mb-4`}>{service.description}</p>
                    <div className="flex items-center justify-between mt-auto">
                      <span className={`text-2xl font-bold ${theme.text}`}>
                        {service.price} zł
                      </span>
                      <span className={`text-sm ${theme.muted}`}>
                        {service.duration} min
                      </span>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
            
            {/* Booking CTA */}
            <div className="text-center mt-10">
              <Link to={`/booking/${subdomain}`}>
                <Button size="lg" style={{ backgroundColor: accentColor }}>
                  <Calendar className="w-4 h-4 mr-2" />
                  Zarezerwuj sesję
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      {settings.show_contact !== false && (
        <section id="contact" className={`py-20 px-6 ${theme.card}`}>
          <div className="max-w-xl mx-auto text-center">
            <h2 className={`text-3xl font-bold mb-6 ${theme.text}`}>Kontakt</h2>
            <p className={`${theme.muted} mb-8`}>
              Chętnie odpowiem na Twoje pytania i pomogę w organizacji sesji.
            </p>
            
            <div className="space-y-4">
              {photographer.email && (
                <a 
                  href={`mailto:${photographer.email}`}
                  className={`flex items-center justify-center gap-3 p-4 rounded-lg ${theme.bg} ${theme.border} border hover:border-opacity-50 transition-colors`}
                >
                  <Mail className="w-5 h-5" style={{ color: accentColor }} />
                  <span className={theme.text}>{photographer.email}</span>
                </a>
              )}
              
              <Link 
                to={`/booking/${subdomain}`}
                className={`flex items-center justify-center gap-3 p-4 rounded-lg border transition-colors`}
                style={{ backgroundColor: accentColor }}
              >
                <Calendar className="w-5 h-5" />
                <span className="font-medium">Umów spotkanie</span>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className={`py-8 px-6 border-t ${theme.border}`}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5" style={{ color: accentColor }} />
            <span className={theme.text}>{photographer.name}</span>
          </div>
          <p className={`text-sm ${theme.muted}`}>
            Powered by <a href="/" className="hover:underline">Focus Photo</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
